/* eslint-disable camelcase */
import React from 'react'
import Image from 'next/image'
import { PrismicRichText } from '@prismicio/react'
import type { Content } from '@prismicio/client'
import type { SliceComponentProps } from '@prismicio/react'
import { resolveTemplateVariables } from '@lib/utils'
import PricingCard from './PricingCard'

interface ShopifyProduct {
	id: string
	title: string
	price: string
	compareAtPrice?: string
	availableForSale: boolean
	requiresSellingPlan: boolean
	variants: {
		id: string
		title?: string
	}[]
}

/**
 * Props for `SubsDetailPricing`.
 */
export type SubsDetailPricingProps = SliceComponentProps<Content.SubsDetailPricingSlice> & {
	hideChoosePlan?: boolean
	productsWithSubscriptions?: Array<{
		product: ShopifyProduct
		subscription:
			| {
					pricePerMonth: number
					currencyCode: string
					sellingPlanId: string
					frequencyText: string
					pricePerBox: number
					compareAtPricePerBox: number | null
			  }
			| undefined
	}>
}

/**
 * Component for "SubsDetailPricing" Slices.
 */
function SubsDetailPricing({
	slice,
	productsWithSubscriptions = [],
	hideChoosePlan = false
}: SubsDetailPricingProps) {
	const { primary } = slice
	const { pricing_title, pricing_subtitle, pricing_image, cta_choose_plan, pricing_cards } = primary

	// Match pricing cards with their corresponding products and subscriptions
	const pricingCardsWithData = React.useMemo(
		() =>
			(pricing_cards || [])
				.map((card) => {
					const subscriptionData = productsWithSubscriptions.find((item) => {
						// Add null check for product.id
						if (!item?.product?.id) {
							console.warn(`Product ID is undefined for item:`, item)
							return false
						}
						return item.product.id.endsWith(card.pricing_card_shopify_product_id || '')
					})

					if (!subscriptionData) {
						console.warn(`Product not found for ID: ${card.pricing_card_shopify_product_id}`)
						return null
					}

					// Debug: Log subscription data to see compareAtPricePerBox
					console.log(`[SubsDetailPricing] Subscription data for ${card.pricing_card_shopify_product_id}:`, {
						title: card.pricing_card_title,
						subscription: subscriptionData.subscription,
						product: {
							id: subscriptionData.product.id,
							price: subscriptionData.product.price,
							compareAtPrice: subscriptionData.product.compareAtPrice
						}
					})

					// Create item object for template variable resolution
					const item = {
						product: subscriptionData.product,
						subscription: subscriptionData.subscription
					}

					return {
						card: {
							...card,
							// Resolve template variables in card fields
							pricing_card_title: resolveTemplateVariables(card.pricing_card_title, item),
							pricing_card_charge_frequency: resolveTemplateVariables(
								card.pricing_card_charge_frequency,
								item
							),
							pricing_card_tag: card.pricing_card_tag
								? resolveTemplateVariables(card.pricing_card_tag, item)
								: null
						},
						product: {
							...subscriptionData.product,
							name: resolveTemplateVariables(card.pricing_card_title, item) || ''
						},
						subscription: subscriptionData.subscription
					}
				})
				.filter((item): item is NonNullable<typeof item> => !!item),
		[pricing_cards, productsWithSubscriptions]
	)

	if (pricingCardsWithData.length === 0 || hideChoosePlan) {
		return null
	}

	// Check if any card has a discount
	// Check both subscription compareAtPricePerBox and product compareAtPrice
	const hasAnyDiscount = pricingCardsWithData.some(({ subscription, product }) => {
		// Check if subscription has compareAtPricePerBox
		if (subscription?.compareAtPricePerBox != null) {
			return true
		}
		
		// Fallback: Check if product has compareAtPrice higher than price
		if (product?.compareAtPrice && product?.price && subscription?.pricePerBox) {
			const productPrice = parseFloat(product.price)
			const productCompareAtPrice = parseFloat(product.compareAtPrice)
			// Only return true if there's actually a discount (compareAtPrice > price)
			return productCompareAtPrice > productPrice
		}
		
		return false
	})

	return (
		<section
			data-slice-type={slice.slice_type}
			data-slice-variation={slice.variation}
			id="choose-plan"
			className="md:p-5"
		>
			<div className="w-full bg-bbx-pearl pb-0 pt-16 md:py-16 font-sans scroll-mt-[62px] md:scroll-mt-[76px]">
				<div className="max-w-7xl mx-auto px-4 flex flex-col items-center">
					{/* Title and Subtitle Centered at Top */}
					<div className="text-4xl mb-4 text-center font-serif">
						<PrismicRichText field={pricing_title} />
					</div>
					<p className="text-xl mb-10 md:whitespace-pre-line text-center max-w-xl mx-auto">
						{pricing_subtitle}
					</p>
					{/* Two-Column Layout: Plan Cards (Left), Image (Right) */}
					<div className="w-full flex flex-col md:flex-row gap-10 md:gap-16 items-center md:items-stretch justify-center pb-20">
						{/* Left: Plan Cards */}
						<div className="flex-1 w-full max-w-xl flex flex-col gap-2.5 justify-center">
							{pricingCardsWithData.map(({ card, product, subscription }) => (
								<PricingCard
									key={card.pricing_card_shopify_product_id || ''}
									title={card.pricing_card_title || ''}
									chargeFrequency={card.pricing_card_charge_frequency || ''}
									tag={card.pricing_card_tag}
									product={product}
									subscription={subscription}
									ctaText={cta_choose_plan || 'Choose Plan'}
								/>
							))}
						</div>
						{/* Right: Plan Image */}
						<div className="hidden flex-1 md:flex justify-center items-center w-full h-full">
							<div className="relative w-full aspect-square bg-gray-50">
								{pricing_image?.url && (
									<Image
										src={pricing_image.url}
										alt={pricing_image.alt || ''}
										fill
										className="object-contain"
										sizes="(max-width: 768px) 100vw, 768px"
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

export default SubsDetailPricing
